import find from 'lodash/find'
import {Languages, type LangT} from '@frontrow/console/src/entities/helpers/languages'
import {
  type ParserT,
  Parser,
  string,
  record,
  array,
  number,
  boolean,
  stringEnum,
  nullable,
  date
} from '@freckle/parser'
import {
  type IdentityProviderT,
  parser as identityProviderParser
} from '@frontrow/console/src/entities/helpers/identity-provider'
import {fromJust} from '@freckle/maybe'
import {Environments, type Environment} from '@frontrow/console/src/entities/helpers/environment'

import {TeacherRoles, type TeacherRole} from '@frontrow/console/src/entities/helpers/teacher-role'
import {Subjects, type SubjectT} from '@frontrow/console/src/entities/helpers/subjects'
import StorageHelper from '@frontrow/console/src/entities/helpers/storage-helper'
import {
  ElaLevelingPrograms,
  type ElaLevelingProgramT
} from '@frontrow/console/src/entities/logic/ela-leveling-program'
import {type FancyOk, type fancyOperations} from '@frontrow/console/src/open-api/clients'

type StudentNameSortFieldT = 'first_name' | 'last_name'

const StudentNameSortField = {
  FirstName: 'first_name' as StudentNameSortFieldT,
  LastName: 'last_name' as StudentNameSortFieldT,

  all(): Array<StudentNameSortFieldT> {
    return [StudentNameSortField.FirstName, StudentNameSortField.LastName]
  },
  parse(s: string): StudentNameSortFieldT | undefined | null {
    return find(StudentNameSortField.all(), sortField => sortField === s)
  },
  parseThrows(s: string): StudentNameSortFieldT {
    return fromJust(
      StudentNameSortField.parse(s),
      `Unexpected value for StudentNameSortFieldT: ${s}`
    )
  },
  forDisplay(s: StudentNameSortFieldT): string {
    return s === StudentNameSortField.FirstName ? 'First Name' : 'Last Name'
  }
}

//Only add properties than can be shared across all apps.
export type BaseTeacherAttrs = {
  id: number
  email: string
  emailVerified: boolean
  firstName: string
  lastName: string
  language: LangT
  countryCode: string | undefined | null
  createdAt: Date
  updatedAt: Date
  deletedAt: Date | undefined | null
  schoolId: number | undefined | null
  teamMode: boolean
  focusMode: boolean
  googleOnly: boolean
  canChangeEmail: boolean
  environment: Environment | undefined | null
  role: TeacherRole | undefined | null
  cleverTeacherId: string | undefined | null
  classLinkTeacherId: string | undefined | null
  renaissanceRPIdentifier: string | undefined | null
  mathStandardSetId: string // Teacher preference standard set id;
  elaStandardSetId: string | undefined | null
  courseSubjects: Array<SubjectT> | undefined | null
  studentNameSortField: StudentNameSortFieldT
  elaLevelingProgram: ElaLevelingProgramT | undefined | null
  sharedRosteringEnabled: boolean | undefined | null
  identityProvider: IdentityProviderT | undefined | null
  idpManaged: boolean
}

/**
 * A teacher response object. `getSchoolAdminsMeTeachersR` returns these
 * directly and `getTeachersR` nests one per row; the two are the same shape.
 */
export function parse(
  response: FancyOk<fancyOperations['getSchoolAdminsMeTeachersR']>[number]
): BaseTeacherAttrs {
  return Parser.run(response, parseTeacherAttrs)
}

const parseTeacherAttrs: ParserT<BaseTeacherAttrs> = Parser.map(
  record({
    id: number(),
    email: string(),
    emailVerified: boolean(),
    firstName: string(),
    lastName: string(),
    language: Languages.parseT(),
    countryCode: nullable(string()),
    createdAt: date(),
    updatedAt: date(),
    deletedAt: nullable(date()),
    schoolId: nullable(number()),
    teamMode: boolean(),
    focusMode: boolean(),
    googleOnly: boolean(),
    canChangeEmail: boolean(),
    environment: nullable(stringEnum('Environment', Environments.parse)),
    role: nullable(stringEnum('TeacherRole', TeacherRoles.parse)),
    cleverTeacherId: nullable(string()),
    classLinkTeacherId: nullable(string()),
    renaissanceRPIdentifier: nullable(string()),
    mathStandardSetId: string(),
    elaStandardSetId: nullable(string()),
    courseSubjects: nullable(array(stringEnum('SubjectT', Subjects.parse))),
    studentNameSortField: stringEnum('StudentNameSortFieldT', StudentNameSortField.parse),
    elaLevelingProgram: nullable(stringEnum('ElaLevelingProgramT', ElaLevelingPrograms.parse)),
    sharedRosteringEnabled: nullable(boolean()),
    identityProvider: nullable(identityProviderParser),
    idpManaged: boolean()
  }) as ParserT<BaseTeacherAttrs>,
  'checkDemoMode',
  (parsedResponse: BaseTeacherAttrs) => {
    // Hide Teacher's Name and email when in demoMode
    const demoMode = StorageHelper.getLocalStoragePropertyDefault('demoMode', false)
    const updatedResponse = updateBaseResponseProperties(parsedResponse, demoMode)
    return updatedResponse
  }
)

// Because Teacher has different metadata depending on the app
// The following functions expect something in the Shape of the common attributes

function updateBaseResponseProperties<ExtraAttrs, AllAttrs extends BaseTeacherAttrs & ExtraAttrs>(
  teacherAttrs: AllAttrs,
  isDemoMode: boolean
): AllAttrs {
  const updatedResponse = {
    email: isDemoMode ? 'demo@freckle.com' : teacherAttrs.email,
    firstName: isDemoMode ? 'Demo' : teacherAttrs.firstName,
    lastName: isDemoMode ? 'Account' : teacherAttrs.lastName
  }
  return {...teacherAttrs, ...updatedResponse}
}
